import { Box, Divider, Typography, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { strings } from 'localisation';
import { GlobalContext } from 'context/GlobalState';
import { useContext } from 'react';
import PaymentRadioButtons from './PaymentRadioButtons';
import RadioButtonsGroup from './RadioButtonsGroup';
import ProductCard8 from './ProductCard8';
import { currency } from 'constants';
import { routerUrls } from 'constants';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ScrollToTop from './ScrollToTop'; //TODO: FIX PATH
import { cartLineKey } from '../utils/cartUtils';

// The currency code is a unit, not part of the figure, so it reads a step down from the amount.
// Same hierarchy the product page uses for the selected price.
const currencyCodeSx = { fontSize: '0.8rem', fontWeight: 500, letterSpacing: '0.04em' };

/**
 * One line of the totals card: the label, then its EUR amount.
 *
 * The two cells belong to the parent grid rather than to a row of their own, so the
 * amounts line up in a column across every line.
 */
const SummaryRow = ({ label, eur, emphasis = false }) => (
  <React.Fragment>
    <Typography
      color="text.primary"
      sx={
        emphasis
          ? { fontSize: 12, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase' }
          : undefined
      }>
      {label}
    </Typography>

    {/* Deliberately not variant="h5": h1–h6 are Playfair (serif) in the theme, which made the
        total render in a different typeface from every other figure. Every amount - total
        included - shares one size and weight; the total is set apart by its uppercase label
        and the gold rule above it, not by a larger figure. */}
    <Typography
      component="span"
      variant="body1"
      color="price.main"
      sx={{
        justifySelf: 'end',
        whiteSpace: 'nowrap',
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.1
      }}>
      {eur}{' '}
      <Box component="span" sx={currencyCodeSx}>
        {currency.EUR}
      </Box>
    </Typography>
  </React.Fragment>
);

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  eur: PropTypes.string.isRequired,
  emphasis: PropTypes.bool
};

const CheckoutSummary2 = ({
  paymentOptionSelected,
  paymentOptions,
  setPaymentOptionSelected,
  deliveryError = false,
  paymentError = false
}) => {
  const { cartList, shippingOption, lang, deliveryOptions } = useContext(GlobalContext);

  // Product prices and delivery fees are already in EUR.
  const computeOrderEUR = () => {
    let subtotalEUR = 0;
    cartList.forEach((item) => {
      const itemPriceEUR = parseFloat(parseFloat(item.price).toFixed(2));
      const itemTotalEUR = parseFloat((itemPriceEUR * parseInt(item.quantity)).toFixed(2));
      subtotalEUR += itemTotalEUR;
    });
    const deliveryEUR = parseFloat(shippingOption.price_eur || 0);
    const totalEUR = subtotalEUR + deliveryEUR;
    return { subtotalEUR, deliveryEUR, totalEUR };
  };

  const { subtotalEUR, deliveryEUR, totalEUR } = computeOrderEUR();

  return (
    <ScrollToTop>
      <Box>
        <Helmet>
          <title>Данни за Поръчка</title>
          <meta name="robots" content="noindex, follow" />
          <meta name="description" content="направете поръчка" />
          <link rel="canonical" href={window.location.href} />
        </Helmet>
        <Typography variant="h6" color="text.primary" mb={3}>
          {strings[lang].yourOrderDetails}
        </Typography>

        {/* Delivery and payment come first: both change the total, so the customer picks them
            before reading the item list and the figures underneath. */}
        <Box mb={3}>
          <Typography fontWeight={600} color="text.primary" mb={1}>
            {strings[lang].shipping}
          </Typography>
          <RadioButtonsGroup values={deliveryOptions} error={deliveryError} />

          <Divider sx={{ my: 2 }} />

          <Typography fontWeight={600} color="text.primary" mb={1}>
            {strings[lang].paymentMethodLabel}
          </Typography>
          <PaymentRadioButtons
            resultSelected={paymentOptionSelected}
            setResultSelected={setPaymentOptionSelected}
            paymentOptions={paymentOptions}
            error={paymentError}
            key={paymentOptions}
          />
        </Box>

        {/* Totals sit right under the two choices, so the customer sees the figures update the
            moment they pick a delivery option - before scrolling through the item list. */}
        <Box
          sx={{
            mb: 3,
            px: 2.5,
            py: 2.25,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            columnGap: 1.25,
            rowGap: 1.25,
            alignItems: 'baseline',
            fontVariantNumeric: 'tabular-nums'
          }}>
          <SummaryRow label={strings[lang].subtotal} eur={subtotalEUR.toFixed(2)} />

          <SummaryRow label={strings[lang].shipping} eur={deliveryEUR.toFixed(2)} />

          {/* A gold rule, not a grey one: the total is the line the eye should land on. */}
          <Box
            sx={{
              gridColumn: '1 / -1',
              height: '1px',
              backgroundColor: 'gold.fill',
              opacity: 0.45,
              my: 0.75
            }}
          />

          <SummaryRow emphasis label={strings[lang].total} eur={totalEUR.toFixed(2)} />
        </Box>

        <Divider
          sx={{
            mb: '1.5rem'
          }}
        />

        <Grid item>
          {cartList.map((item) => (
            <Link
              key={cartLineKey(item)}
              to={routerUrls.product + '/' + item.slug_bg + '/' + item.id} //here we do the connection with the ProductView
              style={{ color: 'inherit', textDecoration: 'none' }}>
              <ProductCard8 item={item} />
            </Link>
          ))}
        </Grid>
      </Box>
    </ScrollToTop>
  );
};
export default CheckoutSummary2;
